import { createMemo, Show } from "solid-js";
import ShapBeeswarm from "../charts/ShapBeeswarm";
import Loading from "../utilities/Loading";
import {
  beeswarmFeaturesFromCell,
  beeswarmFitRatingFromCell,
  beeswarmMissingSnapshotMessage,
  type PerformanceDriversBeeswarmCell,
  type PerformanceDriversBeeswarmKind,
} from "../../utils/performanceDriversBeeswarm";
import { performanceDriversTrustBadgeClass } from "../../utils/performanceDriversModelFit";
import {
  filterPerformanceDriversFeatures,
} from "../../utils/performanceDriversFeatures";
import {
  performanceDriversColorLegendHigh,
  performanceDriversColorLegendLow,
  performanceDriversBeeswarmExplanation,
  performanceDriversBeeswarmImportanceRankingExplanation,
  performanceDriversHighlightSettingsLabel,
} from "../../utils/performanceDriversExplanation";

export interface PerformanceDriversBeeswarmSectionProps {
  kind: PerformanceDriversBeeswarmKind;
  title: string;
  cell: PerformanceDriversBeeswarmCell | null;
  displayNames?: Record<string, string>;
  targetUnit: string;
  loading?: boolean;
  error?: string | null;
  hideDominantDrivers?: boolean;
  onHideDominantDriversChange?: (checked: boolean) => void;
  maneuverLabel?: string;
  /** When false, omit inline model-fit block (page-level fit summary owns layout). */
  showModelFit?: boolean;
}

export default function PerformanceDriversBeeswarmSection(
  props: PerformanceDriversBeeswarmSectionProps
) {
  const rawFeatures = createMemo(() =>
    beeswarmFeaturesFromCell(props.cell, props.displayNames)
  );

  const chartFeatures = createMemo(() =>
    filterPerformanceDriversFeatures(
      rawFeatures(),
      props.hideDominantDrivers ?? false,
      props.kind
    )
  );

  const fitRating = createMemo(() => beeswarmFitRatingFromCell(props.cell));
  const sectionExplanation = createMemo(() =>
    performanceDriversBeeswarmExplanation({
      kind: props.kind,
      targetUnit: props.targetUnit,
      maneuverLabel: props.maneuverLabel,
    })
  );

  return (
    <section class="performance-drivers-table-section performance-drivers-beeswarm-section">
      <div class="performance-drivers-section-heading">
        <h3 class="performance-drivers-section-title">{props.title}</h3>
        <Show when={fitRating()}>
          <span
            class={`performance-drivers-stat-badge ${performanceDriversTrustBadgeClass(fitRating()!.rating)}`}
            title={fitRating()!.hint}
          >
            {fitRating()!.label.toUpperCase()}
          </span>
        </Show>
      </div>
      <p class="performance-drivers-section-explanation">{sectionExplanation()}</p>
      <p class="performance-drivers-section-explanation performance-drivers-beeswarm-ranking-note">
        {performanceDriversBeeswarmImportanceRankingExplanation()}
      </p>

      <Show when={props.loading}>
        <div class="performance-drivers-beeswarm-loading">
          <Loading fullScreen={false} />
        </div>
      </Show>

      <Show when={!props.loading && props.error}>
        <p class="performance-drivers-error" role="alert">
          {props.error}
        </p>
      </Show>

      <Show when={!props.loading && !props.error}>
        <Show
          when={props.cell && !props.cell.skipped && rawFeatures().length > 0}
          fallback={
            <p class="performance-drivers-empty">
              {props.cell?.skip_reason ??
                beeswarmMissingSnapshotMessage(props.kind, props.maneuverLabel)}
            </p>
          }
        >
          <div class="performance-drivers-beeswarm-block">
            <div class="shap-beeswarm-color-legend" aria-hidden="true">
              <span class="shap-beeswarm-legend-label shap-beeswarm-legend-label-low">
                {performanceDriversColorLegendLow()}
              </span>
              <span class="shap-beeswarm-legend-bar" />
              <span class="shap-beeswarm-legend-label shap-beeswarm-legend-label-high">
                {performanceDriversColorLegendHigh()}
              </span>
            </div>

            <Show
              when={chartFeatures().length > 0}
              fallback={
                <p class="performance-drivers-empty">
                  No settings to show. Uncheck Highlight settings to see all channels.
                </p>
              }
            >
              <ShapBeeswarm features={chartFeatures()} targetUnit={props.targetUnit} />
            </Show>
          </div>

          <Show when={props.kind === "static" && props.onHideDominantDriversChange}>
            <div class="performance-drivers-chart-footer">
              <label class="performance-drivers-hide-dominant race-summary-deltas-checkbox">
                <input
                  type="checkbox"
                  checked={props.hideDominantDrivers ?? false}
                  onChange={(e) =>
                    props.onHideDominantDriversChange?.(e.currentTarget.checked)
                  }
                />
                {performanceDriversHighlightSettingsLabel()}
              </label>
            </div>
          </Show>
        </Show>
      </Show>
    </section>
  );
}
